import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, IndianRupee, Copy, Check, Heart, Building2, Flame, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdoptModal({ open, onClose, restroom }) {
  const [tab, setTab] = useState("upi");      // "upi" | "form"
  const [form, setForm] = useState({
    sponsor_name: "",
    sponsor_company: "",
    sponsor_email: "",
    sponsor_phone: "",
    duration_months: 1,
    includes_incinerator: false,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setResult(null);
      setCopied(false);
    }
  }, [open]);

  if (!open || !restroom) return null;

  const amount = 10000 * (form.duration_months || 1);

  const submit = async (method) => {
    if (!form.sponsor_name || !form.sponsor_email) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      // Build payload but DROP empty / null optional fields so they don't fail
      // backend validators that don't treat '' as missing.
      const raw = {
        ...form,
        restroom_id: restroom.restroom_id,
        amount_inr: amount,
        payment_method: method,
      };
      const payload = Object.fromEntries(
        Object.entries(raw).filter(([, v]) => v !== "" && v !== null && v !== undefined)
      );
      const r = await axios.post(`${API}/sponsorships`, payload);
      setResult(r.data);
      toast.success(method === "upi_pending" ? "Pay via UPI to activate" : "Thanks! We'll contact you for the invoice.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Pydantic returns an array of validation errors; surface the first one.
      let msg = "Could not submit";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length) {
        const first = detail[0];
        const field = Array.isArray(first.loc) ? first.loc[first.loc.length - 1] : "field";
        msg = `${field}: ${first.msg || "invalid"}`;
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1100] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center"
        onClick={onClose}
        data-testid="adopt-modal"
      >
        <motion.div
          initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl shadow-2xl max-h-[92vh] overflow-y-auto"
        >
          <div className="flex items-center justify-between p-4 border-b border-slate-100 sticky top-0 bg-white z-10">
            <div className="flex items-center gap-2">
              <Heart size={20} className="text-rose-600 fill-rose-100" />
              <h2 className="font-bold text-lg">Adopt this restroom</h2>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-full" data-testid="adopt-close">
              <X size={20} />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
              <p className="text-xs text-slate-500">You're sponsoring</p>
              <p className="font-semibold text-slate-900">{restroom.name}</p>
              <p className="text-xs text-slate-500">{restroom.address}</p>
            </div>

            {!result && (
              <>
                {/* Pitch */}
                <div className="text-sm text-slate-600 leading-relaxed">
                  <p>
                    For <b>₹10,000 / month</b> your brand keeps this restroom clean, well-lit
                    and visible to thousands. Loocator for India will show an "Adopted by …" badge on
                    the map and on this page.
                  </p>
                  <label className="flex items-center gap-2 mt-3 text-sm">
                    <input
                      type="checkbox"
                      checked={form.includes_incinerator}
                      onChange={(e) => setForm({ ...form, includes_incinerator: e.target.checked })}
                      data-testid="adopt-incinerator-checkbox"
                    />
                    <Flame size={14} className="text-orange-500" />
                    <span>Also install a sanitary-napkin incinerator (recommended for women's facilities)</span>
                  </label>
                </div>

                {/* Duration */}
                <div>
                  <label className="text-xs font-semibold text-slate-500">DURATION</label>
                  <div className="flex gap-2 mt-2">
                    {[1, 3, 6, 12].map((m) => (
                      <button
                        key={m}
                        onClick={() => setForm({ ...form, duration_months: m })}
                        className={`flex-1 py-2 rounded-lg text-sm font-semibold border ${
                          form.duration_months === m
                            ? "bg-rose-600 text-white border-rose-600"
                            : "bg-white text-slate-700 border-slate-200 hover:border-rose-400"
                        }`}
                        data-testid={`adopt-duration-${m}`}
                      >
                        {m} mo
                      </button>
                    ))}
                  </div>
                  <p className="text-right text-sm text-slate-500 mt-1">
                    Total: <b className="text-slate-900">₹{amount.toLocaleString("en-IN")}</b>
                  </p>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 border-b border-slate-200">
                  {[
                    { id: "upi", label: "Pay now (UPI)" },
                    { id: "form", label: "CSR / Invoice" },
                  ].map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTab(t.id)}
                      className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${
                        tab === t.id ? "border-rose-600 text-rose-600" : "border-transparent text-slate-500"
                      }`}
                      data-testid={`adopt-tab-${t.id}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* Form */}
                <div className="grid grid-cols-1 gap-2">
                  <Input placeholder="Your name *" value={form.sponsor_name}
                    onChange={(e) => setForm({ ...form, sponsor_name: e.target.value })}
                    data-testid="adopt-name" />
                  <Input placeholder="Company (optional)" value={form.sponsor_company}
                    onChange={(e) => setForm({ ...form, sponsor_company: e.target.value })}
                    data-testid="adopt-company" />
                  <Input type="email" placeholder="Email *" value={form.sponsor_email}
                    onChange={(e) => setForm({ ...form, sponsor_email: e.target.value })}
                    data-testid="adopt-email" />
                  <Input placeholder="Phone (+91 …)" value={form.sponsor_phone}
                    onChange={(e) => setForm({ ...form, sponsor_phone: e.target.value })}
                    data-testid="adopt-phone" />
                  {tab === "form" && (
                    <textarea
                      placeholder="Anything we should know (CSR budget cycle, invoice details, etc.)"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="text-sm border border-slate-200 rounded-md p-2"
                      rows={3}
                      data-testid="adopt-message"
                    />
                  )}
                </div>

                {tab === "upi" ? (
                  <Button
                    disabled={submitting}
                    onClick={() => submit("upi_pending")}
                    className="w-full bg-rose-600 hover:bg-rose-700"
                    data-testid="adopt-submit-upi"
                  >
                    <IndianRupee size={16} className="mr-1" />
                    Generate UPI payment link
                  </Button>
                ) : (
                  <Button
                    disabled={submitting}
                    onClick={() => submit("interest_form")}
                    className="w-full"
                    data-testid="adopt-submit-form"
                  >
                    <Send size={16} className="mr-1" />
                    Request CSR invoice
                  </Button>
                )}
              </>
            )}

            {result && result.upi_url && (
              <div className="space-y-3" data-testid="adopt-upi-result">
                <p className="text-sm font-semibold text-slate-900">Pay via UPI</p>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&margin=10&data=${encodeURIComponent(result.upi_url)}`}
                  alt="UPI QR"
                  className="w-48 h-48 mx-auto rounded-lg bg-white p-2 shadow-sm"
                  data-testid="adopt-qr"
                />
                <div className="flex gap-2">
                  <a
                    href={result.upi_url}
                    className="flex-1 inline-flex items-center justify-center bg-rose-600 text-white rounded-md py-2 font-semibold"
                    data-testid="adopt-upi-deeplink"
                  >
                    Open UPI app
                  </a>
                  <Button variant="outline" onClick={() => copy(result.upi_id)} data-testid="adopt-copy-upi">
                    {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
                    UPI ID
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  After payment, the admin verifies your txn ref and activates the sponsorship.
                </p>
              </div>
            )}

            {result && !result.upi_url && (
              <div className="bg-emerald-50 text-emerald-800 rounded-xl p-4 border border-emerald-200" data-testid="adopt-form-result">
                <p className="font-semibold">Got it ❤️</p>
                <p className="text-sm mt-1">
                  We'll reach out at <b>{form.sponsor_email}</b> within 2 business days
                  with the invoice + CSR-ready receipt.
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
